import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Select,
  MenuItem,
  Tooltip
} from '@mui/material';
import { Edit as EditIcon, AutoAwesome as AutoAwesomeIcon, CalendarMonth as CalendarIcon } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import ActionItemDrawer from './ActionItemDrawer';
import AIActionItemSuggestions from './AIActionItemSuggestions';
//import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
//import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import emptyStateImage from '../../assets/EmptyAction.png';
//import { format } from 'date-fns';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';



export default function ActionPlan({ 
  careerData, 
  onUpdate,
  onGoToGoal
}) {
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isAIDialogOpen, setIsAIDialogOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const newActionItemRefs = useRef({});
  const location = useLocation();
  const actionItemRef = useRef(null);

  useEffect(() => {
    const handleInitialFocus = () => {
      const { state } = window.history;
      if (state?.scrollToGoal) {
        const goalElement = document.getElementById(`goal-${state.scrollToGoal}`);
        if (goalElement) {
          goalElement.scrollIntoView({ behavior: 'smooth' });
          if (state.focusActionItem) {
            setTimeout(() => {
              const inputElement = goalElement.querySelector('input[type="text"]');
              if (inputElement) {
                inputElement.focus();
              }
            }, 100);
          }
        }
      }
    };

    handleInitialFocus();
  }, []);

  useEffect(() => {
    if (location.state && location.state.focusActionItem) {
      setTimeout(() => {
        if (actionItemRef.current) {
          actionItemRef.current.focus();
        }
      }, 0);
    }
  }, [location.state]);

  const handleGoToGoal = (goalId) => {
    onGoToGoal(goalId);
  };

  const handleStatusChange = (goalId, itemId, newStatus) => {
    const updatedGoals = careerData.developmentGoals.map(goal => {
      if (goal.id === goalId) {
        const updatedItems = goal.actionItems.map(item => 
          item.id === itemId ? { ...item, status: newStatus } : item
        );
        return { ...goal, actionItems: updatedItems };
      }
      return goal;
    });

    onUpdate({
      ...careerData,
      developmentGoals: updatedGoals
    });
  };

  const formatDate = (date) => {
    if (!date) return '';
    let dateObj = date;
    if (date?.toDate) { // Handle Firestore timestamp
      dateObj = date.toDate();
    } else if (!(date instanceof Date)) {
      dateObj = new Date(date);
    }
    return format(dateObj, 'MMM d');
  };

  const handleNewActionItem = (goalId, event, value) => {
    if (event.key === 'Enter' && value.trim()) {
      const updatedGoals = careerData.developmentGoals.map(goal => {
        if (goal.id === goalId) {
          const newItem = {
            id: Date.now().toString(),
            text: value.trim(),
            status: 'On track',
            dueDate: new Date(),
            notes: ''
          };
          return {
            ...goal,
            actionItems: [...(goal.actionItems || []), newItem]
          };
        }
        return goal;
      });

      onUpdate({
        ...careerData,
        developmentGoals: updatedGoals
      });

      // Clear the input
      event.target.value = '';
    }
  };

  const handleItemUpdate = (goalId, itemId, updatedItem) => {
    const updatedGoals = careerData.developmentGoals.map(goal => {
      if (goal.id === goalId) {
        // If itemId doesn't exist in current items, this is a new item
        if (!goal.actionItems?.find(item => item.id === itemId)) {
          return {
            ...goal,
            actionItems: [...(goal.actionItems || []), { id: itemId, ...updatedItem }]
          };
        }
        // Otherwise update existing item
        const updatedItems = goal.actionItems.map(item => 
          item.id === itemId ? { ...item, ...updatedItem } : item
        );
        return { ...goal, actionItems: updatedItems };
      }
      return goal;
    });

    onUpdate({
      ...careerData,
      developmentGoals: updatedGoals
    });
  };

  const handleItemDelete = (goalId, itemId) => {
    const updatedGoals = careerData.developmentGoals.map(goal => {
      if (goal.id === goalId) {
        return {
          ...goal,
          actionItems: goal.actionItems.filter(item => item.id !== itemId)
        };
      }
      return goal;
    });

    onUpdate({
      ...careerData,
      developmentGoals: updatedGoals
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'On track': return '#CAF8C9';
      case 'At risk': return '#FFE2C8';
      case 'Off track': return '#FFBEBE';
      default: return '#34D399';
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 1 }}>Action plan</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        A 12-month development action plan. Over-emphasize experiences and make sure your actions are SMART: Specific, Measurable, Achievable, Relevant, and Time-bound.
      </Typography>

      {(!careerData?.developmentGoals || careerData.developmentGoals.length === 0) ? (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
          flexDirection: 'column',
          gap: 2
        }}>
          {/* Placeholder for empty state image */}
          <Box sx={{ mb: 2 }}>
            <img src={emptyStateImage} alt="No goals yet" />
          </Box>
          <Typography variant="h6" color="text.secondary">
            No Development Goals Yet
          </Typography>
          <Typography color="text.secondary">
            Start by adding development goals to create your action plan
          </Typography>
        </Box>
      ) : (
        careerData?.developmentGoals?.map(goal => (
          <Box 
            key={goal.id} 
            id={`goal-${goal.id}`}
            sx={{ mb: 4 }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Goal: {goal.goal}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleGoToGoal(goal.id)}
                sx={{ 
                  textTransform: 'none', 
                  whiteSpace: 'nowrap',
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  minWidth: 'fit-content',
                  marginLeft: '16px',
                  marginRight: '16px',
                  color: 'text.secondary',
                  backgroundColor: 'white',
                  borderRadius: '20px',
                  border: '1px solid',
                  borderColor: '#cbcbcb',
                  '&:hover': {
                      backgroundColor: '#ffffff',
                      borderColor: '#2D68FE',
                      color: '#242424'
                  }
               }}
              >
                Go to the goal
              </Button>
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell width="65%" style={{ fontWeight: 'bold' }}>Action item</TableCell>
                    <TableCell width="20%" style={{ fontWeight: 'bold' }}>Deadline</TableCell>
                    <TableCell width="15%" style={{ fontWeight: 'bold' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {goal.actionItems?.map((item) => (
                    <TableRow
                      key={item.id}
                      onClick={() => {
                        setSelectedItem(item);
                        setSelectedGoal(goal);
                        setIsDrawerOpen(true);
                      }}
                      onMouseEnter={() => setHoveredRow(`${goal.id}-${item.id}`)}
                      onMouseLeave={() => setHoveredRow(null)}
                      sx={{ 
                        cursor: 'pointer',
                        height: '10px',
                        '&:hover': {
                          backgroundColor: '#f4f4f4'
                        },
                        '&:hover .action-edit': {
                          opacity: 1
                        }
                      }}
                    >

                      
                      <TableCell sx={{ paddingTop: '12px', paddingBottom: '12px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          {item.text}
                                                  
                          
                          <Box 
                            className="action-edit"
                            sx={{ 
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              width: '24px',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <EditIcon 
                              sx={{ 
                                fontSize: '20px',
                                color: 'action.active'
                              }}
                            />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          paddingTop: '12px', 
                          paddingBottom: '12px'
                        }}
                        onClick={(e) => e.stopPropagation()} 
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {formatDate(item.dueDate)}
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                              value={item.dueDate}
                              onChange={(newDate) => {
                                if (newDate) {
                                  handleItemUpdate(goal.id, item.id, { ...item, dueDate: newDate });
                                }
                              }}
                              renderInput={(props) => (
                                <TextField 
                                  {...props} 
                                  variant="standard" 
                                  sx={{ 
                                    width: '0px', 
                                    border: 'none',
                                    '& .MuiInput-underline:before': { borderBottom: 'none' },
                                    '& .MuiInput-underline:after': { borderBottom: 'none' },
                                  }} 
                                />
                              )}
                              components={{
                                OpenPickerIcon: () => (
                                  <CalendarIcon 
                                    sx={{ 
                                      fontSize: '20px',
                                      color: 'action.active',
                                      opacity: 0.7,
                                      cursor: 'pointer',
                                      '&:hover': {
                                        opacity: 1
                                      }
                                    }}
                                  />
                                )
                              }}
                            />
                          </LocalizationProvider>
                        </Box>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()} sx={{ paddingTop: '12px', paddingBottom: '12px' }}>
                        <Select
                          value={item.status}
                          onChange={(e) => handleStatusChange(goal.id, item.id, e.target.value)}
                          
                          size="small"
                          sx={{
                              borderRadius: '20px',
                              border: '0px solid',
                              background: getStatusColor(item.status),
                              height: '32px',
                              width: '120px',
                              border: 'none',
                            '& .MuiOutlinedInput-notchedOutline': {
                              color: 'black',//getStatusColor(item.status),
                              fontWeight: 500,
                              border: 'none',
                            }
                          }}
                        >
                          <MenuItem value="On track">On track</MenuItem>
                          <MenuItem value="At risk">At risk</MenuItem>
                          <MenuItem value="Off track">Off track</MenuItem>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} sx={{ position: 'relative', paddingTop: '0px', paddingBottom: '0px' }} >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                          fullWidth
                          placeholder="Add new action item"
                          inputRef={el => newActionItemRefs.current[goal.id] = el}
                          onKeyPress={(e) => handleNewActionItem(goal.id, e, e.target.value)}
                          sx={{
                              padding: '0px',
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { border: 'none' }
                            }
                          }}
                        />
                        <Button
                          variant="text"
                          startIcon={<AutoAwesomeIcon />}
                          onClick={() => {
                            setSelectedGoal(goal);
                            setIsAIDialogOpen(true);
                          }}
                          sx={{
                            backgroundColor: 'grey.100',
                            width: '220px',
                            height: '42px',
                            color: 'text.primary',
                            textTransform: 'none',
                            padding: '6px 16px',
                            border: '1px solid transparent',
                            '&:hover': {
                              backgroundColor: 'common.white',
                              border: '1px solid',
                              borderColor: 'primary.main',
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: '1.2rem',
                            }
                          }}
                        >
                          Generate with AI
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))
      )}

      <ActionItemDrawer
        open={isDrawerOpen}
        actionItem={selectedItem}
        goalTitle={selectedGoal?.goal}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedItem(null);
          setSelectedGoal(null);
        }}
        onUpdate={(updatedItem) => {
          if (selectedGoal && selectedItem) {
            handleItemUpdate(selectedGoal.id, selectedItem.id, updatedItem);
          }
        }}
        onDelete={() => {
          if (selectedGoal && selectedItem) {
            handleItemDelete(selectedGoal.id, selectedItem.id);
            setIsDrawerOpen(false);
            setSelectedItem(null);
            setSelectedGoal(null);
          }
        }}
      />

      <AIActionItemSuggestions
        open={isAIDialogOpen}
        onClose={() => {
          setIsAIDialogOpen(false);
          setSelectedGoal(null);
        }}
        goal={selectedGoal}
        existingItems={selectedGoal?.actionItems || []}
        onSelect={(suggestion) => {
          if (selectedGoal) {
            const newItem = {
              id: Date.now().toString(),
              ...suggestion,
              status: 'On track'  // Ensure status is set
            };
            handleItemUpdate(selectedGoal.id, newItem.id, newItem);
          }
        }}
      />
    </Box>
  );
} 